import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { GroupBase, OptionsOrGroups } from "react-select";
import { toast } from "react-toastify";
import { useDebouncedCallback } from "use-debounce";
import {
  useLazyOrganizationVehiclesQuery,
  useOrganizationGroupsQuery,
} from "../../../api/network/adminApiServices";
import {
  reducerPath,
  useGetDeviceListingQuery,
  useLazyGetDeviceListingQuery,
} from "../../../api/network/camerasAdminVMApiService";
import { useLazyGReverseGeocodeQuery } from "../../../api/network/mapApiServices";
import {
  CamerasAdminVMApiTags,
  useGetAllAvailableDeviceModelsQuery,
} from "../../../api/network/vmApiService";
import { setModalsData, TModalsState } from "../../../api/store/commonSlice";
import {
  CameraListingPayload,
  CameraListingResponse,
  DeviceListingResponse,
  DeviceListingResponseWAddress,
} from "../../../api/types/Camera";
import { Model } from "../../../api/types/CamerasAdminVM";
import { LegacyGroupEntityListingPayload } from "../../../api/types/Group";
import { TLegacyVehicle } from "../../../api/types/Vehicle";
import svgIcons from "../../../assets/svg/svgIcons";
import LiveViewIcon from "../../../assets/svg/video-icon.svg";
import {
  AdminFormFieldAsyncLazyDropdown,
  AdminFormFieldDropdown,
  AdminFormFieldSubmit,
  TSelectboxOption,
} from "../../../components/admin/formFields";
import AdminTable from "../../../components/adminTable";
import { TGroupSelectorItem } from "../../../components/common/groupSelector.type";
import {
  EditListingColumnsButton,
  ModalEditListingColumns,
  TListingColumn,
} from "../../../components/modalEditListingColumns";
import Pagination, {
  TPaginationSelected,
} from "../../../components/pagination";
import AppSearchBox from "../../../components/searchBox";
import { TAdminTableRowData } from "../../../components/types";
import { APP_CONFIG } from "../../../constants/constants";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { routeUrls } from "../../../navigation/routeUrls";
import { SortingStatusWithReset } from "../../../utils/adminListings";
import { asyncDebounce } from "../../../utils/common";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../../utils/localStorageItems";
import { useLoggedInUserData } from "../../../utils/user";
import { ROLE_ACCESS_LEVELS } from "../../admin-details/role/constants";
import {
  all_columns,
  all_columns_widget,
  CONVERT_CAMERA_MODEL_NAMES,
  REPORT_FILTER_BY_OPTION_VALUES,
  STATUS_OPTIONS,
} from "../constants";
import { useCameraAdminContext } from "../context/cameraAdmin.context";
import CameraListingWidgetMap from "../dashboard/cameraListingWidgetMap";
import { TMapRef } from "../dashboard/type";
import ModalGroupCameraSelector from "./modalGroupCameraSelector";
import ModalLinkAsset from "./modalLinkAssetes";
import ParkingPhotos from "./modalParkingPhotos";
import { LegacyVerifyEmailOtpResponseSuccess } from "../../../api/types/Onboarding";
import { TLegacyUserWithAccount } from "../../../api/types/User";

type ScreenCameraManagementProps = {
  displayAsDashWidget?: boolean;
};

const columnOrderingFields: Record<string, string> = {
  "Camera ID": "serial_number",
  "Asset ID": "ft_device_id",
  "Asset Description": "ft_device_description",
  "Display Name": "ft_displayname",
  Driver: "drivername",
  Model: "model",
  Status: "is_online",
  // "Live View" // intentionally excluded from sortability
  // "Actions" // intentionally excluded from sortability
};

const ListItemStatusLabel = ({
  item,
  t,
}: {
  item: DeviceListingResponse;
  t: (key: string) => string;
}) => {
  return (
  item.camera_dashboard_status==="not_initiated" ? (
    <span className="text-field-intermediate">{t("not_initiated")}</span>
  ) : item.camera_dashboard_status === "live" ? (
    <span className="text-field-success">{t("live")}</span>
  ) : item.camera_dashboard_status === "not_reporting" ? (
    <span className="text-field-error-dark">{t("not_reporting")}</span>
  ) : item.camera_dashboard_status === "standby" ? (
    <span className="text-amber-500">{t("standby")}</span>
  ) : (
    <span className="text-field-intermediate">{t("not_initiated")}</span>
  ))
};

const ScreenCameraManagement = ({
  displayAsDashWidget = false,
}: ScreenCameraManagementProps) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "camera.dashboard.management.cameras",
  });
  const {
    allGroupsSelected,
    allCamerasSelected,
    selectedDataType,
    setAllGroupsSelected,
    selectedGroups,
    setSelectedGroups,
    selectedCameras,
  } = useCameraAdminContext();

  const { getLocalStorageItem } = useLocalStorage();
  const loggedInUserData: LegacyVerifyEmailOtpResponseSuccess = useSelector(
    (state: any) => state.commonReducer.user
  );
  const loggedInUser: TLegacyUserWithAccount =
    loggedInUserData?.user ?? getLocalStorageItem(localStorageKeys.user);
  const liveviewDurationSeconds =
    loggedInUser?.account?.cameraliveviewduration ?? 30;

  const [expanded, setExpanded] = useState(true);

  // const [activeRowIndex, setActiveRowIndex] = useState<number | null>(null);
  // const [currentRowData, setCurrentRowData] = useState<DeviceListingResponse>();
  type TActiveRow = {
    index: number | null;
    data: DeviceListingResponse | undefined | null;
  };
  const [activeRow, setActiveRow] = useState<TActiveRow | null>();

  const [searchText, setSearchText] = useState<string>("");
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { getUserACLAccessLevel } = useACL();
  const thisOrgDriverAllowed = useLoggedInUserData("hasdrivers");
  const showDriver = thisOrgDriverAllowed === "1";
  const [isConfigurationModal, setIsConfigurationModal] = useState(false);
  // columns

  // Filter columns based on showDriver
  const filteredColumns = useMemo(
    () =>
      (displayAsDashWidget ? all_columns_widget : all_columns).filter(
        (col) => showDriver || col !== "Driver"
      ),
    [showDriver, displayAsDashWidget]
  );

  const savedColumns = getLocalStorageItem(
    displayAsDashWidget
      ? localStorageKeys.columns.CameraListingWidget
      : localStorageKeys.columns.CameraManagement
  );
  // Update columns state initialization
  const [columns, setColumns] = useState<TListingColumn[]>(
    filteredColumns.map((colItem) => ({
      name: colItem,
      show: !!savedColumns ? savedColumns.includes(colItem) : true,
    }))
  );

  // Update columns when showDriver changes
  useEffect(() => {
    setColumns(
      filteredColumns.map((colItem) => ({
        name: colItem,
        show: !!savedColumns ? savedColumns.includes(colItem) : true,
      }))
    );
  }, [filteredColumns]);

  const [cameraManagementQueryParams, setCameraManagementQueryParams] =
    useState<
      CameraListingPayload & { driver_name?: string; ordering?: string }
    >({
      keyword: "",
      model: "",
      status: "",
      offset: 0,
      limit: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      ...(selectedDataType === REPORT_FILTER_BY_OPTION_VALUES.CAMERA
        ? {
          camera_ids: !!allCamerasSelected
            ? ""
            : selectedCameras?.length
              ? selectedCameras.join(",")
              : "",
        }
        : {
          group_ids: !!allGroupsSelected
            ? ""
            : selectedGroups?.length
              ? selectedGroups.join(",")
              : "",
        }),
      driver_id: undefined,
      driver_name: undefined,
      linked_to_driver: false,
    });

  const handleOrderingChange = (ordering?: string) => {
    setCameraManagementQueryParams((prev) => ({
      ...prev,
      ordering,
      offset: 0,
    }));
  };

  // fetching camera device data
  const {
    data: dataDeviceListing,
    isFetching: isFetchingDeviceListing,
    error,
    refetch,
  } = useGetDeviceListingQuery(
    {
      ...cameraManagementQueryParams,
      offset: cameraManagementQueryParams.offset,
      // (cameraManagementQueryParams.page - 1) *
      // cameraManagementQueryParams.limit,
    },
    {
      refetchOnMountOrArgChange: true, // caching removed to ensure listing always shows correct status
    }
  );

  const [getDeviceListingAPITrigger, {}] = useLazyGetDeviceListingQuery();
  const { data: allAvailableDeviceModels, isFetching: isFetchingDeviceModels } =
    useGetAllAvailableDeviceModelsQuery();

  // check if group hierarchy is enabled
  const accountHasGroupHierarchyEnabled = useLoggedInUserData("groupHierarchy");
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  // fetch groups data
  const {
    data: dataOrgGroups,
    isFetching: isFetchingOrgGroups,
    error: errorOrgGroups,
  } = useOrganizationGroupsQuery(
    {
      accountid: `${thisUserOrganizationId}`,
      page: 1,
      page_size: APP_CONFIG.LISTINGS.XL_PAGE_SIZE,
      search: "",
    } as LegacyGroupEntityListingPayload,
    {
      refetchOnMountOrArgChange: true,
    }
  );
  useEffect(() => {
    setCameraManagementQueryParams((prev) => ({
      ...prev,
      ...(selectedDataType === REPORT_FILTER_BY_OPTION_VALUES.CAMERA
        ? {
          camera_ids: !!allCamerasSelected
            ? ""
            : selectedCameras?.length
              ? selectedCameras.join(",")
              : "",
          group_ids: undefined,
        }
        : {
          group_ids: !!allGroupsSelected
            ? ""
            : selectedGroups?.length
              ? selectedGroups.join(",")
              : "",
          camera_ids: undefined,
        }),
    }));
  }, [selectedGroups, selectedCameras, selectedDataType]);

  // set selected groups
  useEffect(() => {
    if (accountHasGroupHierarchyEnabled === "1") return;
    if (!!dataOrgGroups && !isFetchingOrgGroups) {
      setSelectedGroups(
        dataOrgGroups?.results?.map((group) => {
          return group.groupid;
        }) ?? ([] as string[])
      );
    }
  }, [dataOrgGroups, isFetchingOrgGroups]);

  // update all groups selected state
  useEffect(() => {
    if (accountHasGroupHierarchyEnabled === "1") return;
    if (!!dataOrgGroups && !isFetchingOrgGroups) {
      setAllGroupsSelected(
        dataOrgGroups?.results?.length === selectedGroups?.length
      );
    }
  }, [selectedGroups, dataOrgGroups, isFetchingOrgGroups]);

  // close context menu on unmount
  useEffect(() => {
    return () => {
      dispatch(
        setModalsData({
          ...modalsState,
          showContextMenu: false,
        })
      );
    };
  }, []);

  const handleOptionClick = (
    e: React.MouseEvent<HTMLElement>,
    rowIndex: number
  ) => {
    e.stopPropagation();
    if (modalsState.showContextMenu) {
      dispatch(
        setModalsData({
          ...modalsState,
          showContextMenu: false,
        })
      );
      return;
    }
    // setActiveRowIndex((prev) => (prev === rowIndex ? null : rowIndex));
    // setActiveRowIndex(rowIndex);
    // setCurrentRowData(results?.[rowIndex]);
    flushSync(() =>
      setActiveRow({
        index: rowIndex,
        data: results?.[rowIndex],
      })
    ); // Synchronously set changes
    const target = e.currentTarget as HTMLElement;
    setTimeout(() => {
      dispatch(
        setModalsData({
          ...modalsState,
          showContextMenu: true,
          contextMenuOptions: {
            AnchorRef: target,
            Content: <>{renderPopup(rowIndex)}</>,
            relativePosition:
              // last 3 rows to have menu on top
              rowIndex + 1 > (dataDeviceListing?.data?.length || 3) - 3
                ? "bottom-left"
                : "top-left",
            widthClass: "p-2 min-w-48",
          },
        })
      );
    }, 1000);
  };

  // prepare query params for fetching organization users
  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    setCameraManagementQueryParams({
      ...cameraManagementQueryParams,
      offset: 0,
      keyword: value,
    });
  }, 500);

  const onCameraWakeupSuccess = (
    serialNumber: string,
    mode: "liveview" | "voicecall"
  ) => {
    // refetch camera device listing
    dispatch({
      type: `${reducerPath}/invalidateTags`,
      payload: [CamerasAdminVMApiTags.CAMERA_DEVICE_LISTING],
    });

    setCameraManagementQueryParams({
      ...cameraManagementQueryParams,
      offset: 0,
    });

    setTimeout(() => {
      dispatch(setModalsData({ ...modalsState, showPrimaryOverlay: true }));
      toast.info(
        "Allowing a few seconds for the camera to wake up and connect with servers..."
      );
      setTimeout(() => {
        dispatch(setModalsData({ ...modalsState, showPrimaryOverlay: false }));
        // Initiate Camera live view for currently selected camera
        if (!!serialNumber) {
          dispatch(
            setModalsData({
              ...modalsState,
              showModalWakeupCamera: null,
              ...(mode === "voicecall"
                ? {
                  // the API uses `serial_number` instead of `cid`
                  showModalVMCameraVoicecallSN: serialNumber,

                  // find camera data for voice call and pass asset info
                  voiceCallAssetId: (() => {
                    const cameraData = results?.find(
                      (camera) => camera.serial_number === serialNumber
                    );
                    return cameraData?.ft_device_id || "";
                  })(),
                  voiceCallDriverName: (() => {
                    const cameraData = results?.find(
                      (camera) => camera.serial_number === serialNumber
                    );
                    return (
                      cameraData?.drivername ||
                      cameraData?.driverid ||
                      "Not Available"
                    );
                  })(),
                }
                : {
                  showModalVMCameraLiveviewSN: serialNumber,
                }),
            })
          );
          if (mode === "liveview") {
            setTimeout(() => {
              // Show Info. of live view (That It'll be available for only 10 minutes)
              toast.info(
                t("live_feed_info", {
                  seconds: liveviewDurationSeconds,
                })
              );
            }, 3 * 1000);
          }
        }
      }, 10 * 1000); // allowing 10 seconds
    }, 2500); // extra 2.5 seconds
  };

  const initAppropriateActionToStartLiveview = (row: DeviceListingResponse) => {
    if (row?.is_online === "offline") {
      dispatch(
        setModalsData({
          ...modalsState,
          showModalWakeupCamera: {
            ...row,
            onSuccessCallback: (sn: string) =>
              onCameraWakeupSuccess(sn, "liveview"),
          },
        })
      );
    } else if (row?.is_online === "online" && !!row?.serial_number) {
      dispatch(
        setModalsData({
          ...modalsState,
          showModalVMCameraLiveviewSN: row.serial_number,
        })
      );
    }
  };

  const onLiveViewClick = useCallback(() => {
    // Bug: Due to availability of stale state of active row, using setActiveRow method to retrieve latest
    // state and then setting the same state without changes
    setActiveRow((prevActiveRow) => {
      const row = prevActiveRow?.data;
      if (!!row) {
        setTimeout(() => {
          initAppropriateActionToStartLiveview(row);
        }, 100);
      }
      return prevActiveRow;
    });
  }, [dispatch, modalsState, activeRow]);

  const initAppropriateActionToStartVoicecall = (
    row: DeviceListingResponse
  ) => {
    if (row?.is_online === "offline") {
      dispatch(
        setModalsData({
          ...modalsState,
          showModalWakeupCamera: {
            ...row,
            onSuccessCallback: (sn: string) =>
              onCameraWakeupSuccess(sn, "voicecall"),
            // onFailureCallback: () => {
            //   dispatch(
            //     setModalsData({
            //       ...modalsState,
            //       voiceCallAssetId: null,
            //       voiceCallDriverName: null,
            //     })
            //   );
            // },
          },
          voiceCallAssetId: row.ft_device_id || "",
          voiceCallDriverName:
            row.drivername || row.driverid || "Not Available",
        })
      );
    } else if (row?.is_online === "online" && !!row?.serial_number) {
      dispatch(
        setModalsData({
          ...modalsState,
          showModalVMCameraVoicecallSN: row.serial_number,
          voiceCallAssetId: row.ft_device_id || "",
          voiceCallDriverName:
            row.drivername || row.driverid || "Not Available",
        })
      );
    }
  };

  const onVoicecallClick = useCallback(() => {
    // Bug: Due to availability of stale state of active row, using setActiveRow method to retrieve latest
    // state and then setting the same state without changes
    setActiveRow((prevActiveRow) => {
      const row = prevActiveRow?.data;
      if (!!row) {
        setTimeout(() => {
          initAppropriateActionToStartVoicecall(row);
        }, 100);
      }
      return prevActiveRow;
    });
  }, [dispatch, modalsState, activeRow]);

  const { count, data: results } = dataDeviceListing || {};
  const [gReverseGeocodeAPITrigger] = useLazyGReverseGeocodeQuery();

  // Geocode addresses for each event item

  // Memoize geocoded addresses to avoid unnecessary requests
  const geocodeCache = useRef<{ [key: string]: string }>({});

  // Helper to get address for a given lat/lng using API trigger
  const getAddressForEvent = async (latitude?: number, longitude?: number) => {
    if (!latitude || !longitude) return "";
    const key = `${latitude},${longitude}`;
    if (geocodeCache.current[key]) {
      return geocodeCache.current[key];
    }
    try {
      const { results } = await gReverseGeocodeAPITrigger({
        latitude,
        longitude,
      }).unwrap();
      if (results?.[0]?.formatted_address) {
        geocodeCache.current[key] = results?.[0]?.formatted_address;
        return results?.[0]?.formatted_address;
      }
    } catch (e) {
      // ignore errors, return empty string
    }
    return "";
  };

  // State to hold results with addresses
  const [resultsWithAddress, setResultsWithAddress] = useState<
    DeviceListingResponseWAddress[]
  >([]);

  useEffect(() => {
    if (isFetchingDeviceListing) setResultsWithAddress([]);
  }, [isFetchingDeviceListing]);

  useEffect(() => {
    let isMounted = true;
    if (results && results.length > 0) {
      Promise.all(
        results.map(async (item) => {
          const formatted_address = !!item.ft_deviceaddress
            ? item.ft_deviceaddress
            : await getAddressForEvent(
              item.ft_lastvalidlatitude,
              item.ft_lastvalidlongitude
            );
          return { ...item, formatted_address };
        })
      ).then((updated) => {
        if (isMounted) setResultsWithAddress(updated);
      });
    } else {
      setResultsWithAddress([]);
    }
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results]);

  //table data
  const tableData: TAdminTableRowData[] = !!results
    ? (results || []).map((item, index) => ({
      navLink:
        displayAsDashWidget && !!item.cid
          ? !item.ft_lastvalidlatitude || !item.ft_lastvalidlongitude
            ? false
            : () => {
              // map operation to click on a specific camera on map
              if (APP_CONFIG.DEBUG.ALL)
                console.log(
                  "CLICKED ITEM",
                  item.serial_number,
                  "INDEX",
                  index
                );
              const mapRef = (window as any).map as TMapRef;
              const mPushpinObject = mapRef.objects.mPushpins.find(
                (pushpinItem) => pushpinItem.recordId === item.ft_device_id
              );
              if (APP_CONFIG.DEBUG.ALL)
                console.log("mPushpinObject", mPushpinObject);
              mPushpinObject?.infobox?.open({
                anchor: mPushpinObject?.marker,
                map: mapRef?.map,
              });
            }
          : item.cid
            ? `${routeUrls.dashboardChildren.cameraChildren.camera_management}/${item.cid}__${item.ft_device_id}`
            : false,
      cellData: (displayAsDashWidget
        ? [
          item.serial_number,
          item.ft_device_id,
          item.ft_device_description,
          item.ft_displayname,
          ...(showDriver ? [item.drivername || item.driverid || "-"] : []),
          ListItemStatusLabel({ item, t }),
          <div className="flex gap-2 items-center">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setActiveRow({
                  index: index,
                  data: results?.[index],
                });
                onLiveViewClick?.();
              }}
              className={`h-7 px-3 -ms-4 -my-2 rounded-md flex items-center justify-center gap-2 disabled:opacity-40 ${false && activeRow?.index === index ? "bg-white relative z-modal" : "enabled:hover:bg-accent-blue-pale"}`}
              disabled={!item?.cid}
            >
              <img
                src={svgIcons.LiveViewIcon}
                alt="vertical-options"
                className="cursor-pointer"
              />
              {t("live_view")}
            </button>
          </div>,
        ]
        : [
          item.serial_number,
          item.ft_device_id,
          item.ft_device_description,
          item.ft_displayname,
          ...(showDriver ? [item.drivername || item.driverid || "-"] : []),
          // item.group_name,
          CONVERT_CAMERA_MODEL_NAMES
            ? item.model.replace("K", "FV")
            : item.model,
          ListItemStatusLabel({ item, t }),
          <div className="flex gap-2 items-center">
            <img
              src={svgIcons.ShowImageIcon}
              alt="show-image"
              className={`p-2 bg-gray-100 cursor-pointer rounded-md ${item?.is_online === "offline" && item?.snapshot?.length > 0 ? "hover:bg-accent-blue-pale" : "cursor-not-allowed opacity-50"}`}
              onClick={(e) => {
                e.stopPropagation();
                if (results?.[index]?.snapshot?.length > 0) {
                  setActiveRow({
                    index: index,
                    data: results?.[index],
                  });
                  if (item?.is_online === "offline") {
                    dispatch(
                      setModalsData({
                        ...modalsState,
                        showParkingPhotoView: true,
                      })
                    );
                  }
                }
              }}
            />
            <button
              type="button"
              onClick={(e) => handleOptionClick(e, index)}
              className={`size-7 -my-2 rounded-md flex items-center justify-center ${activeRow?.index === index ? "bg-white relative z-modal" : "enabled:hover:bg-accent-blue-pale"}`}
            >
              <img
                src={svgIcons.MoreVertBlack}
                alt="vertical-options"
                className="cursor-pointer"
              />
            </button>
          </div>,
        ]
      ).filter((_, index) => columns[index].show),
    }))
    : [];

  const menuItems = [
    {
      key: "link_asset",
      icon: svgIcons.LinkIcon,
      onClick: () =>
        dispatch(setModalsData({ ...modalsState, showModalLinkAsset: true })),
    },
    ...(getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_CAMERA_OUTGOING_CALL) >=
      ROLE_ACCESS_LEVELS.READ
      ? [
        {
          key: "voice_call",
          icon: svgIcons.VoiceCallIcon,
          onClick: onVoicecallClick,
        },
      ]
      : []),
    {
      key: "live_view",
      icon: LiveViewIcon,
      onClick: onLiveViewClick,
    },
    // { key: "call_logs", icon: svgIcons.CallLogsIcon }, // Commented for now, as per client feedback
    {
      key: "configuration",
      icon: svgIcons.ConfigurationIcon,
      onClick: () => {
        setActiveRow((previousState) => {
          navigate(
            `${routeUrls.dashboardChildren.cameraChildren.camera_settings}/${previousState?.data?.cid}`,
            {
              state: {
                model: previousState?.data?.model,
                isSingleConfiguration: true,
                cameraName: previousState?.data?.serial_number
              },
            }
          );
          return null;
        });
      },
    },
  ];

  const renderPopup = (rowIndex: number) => {
    if (activeRow === null) return null;

    const cellData = results?.[rowIndex];

    // close context menu
    const closeContextMenu = () => {
      dispatch(
        setModalsData({
          ...modalsState,
          showContextMenu: false,
        })
      );
    };

    return (
      <>
        {/* <div
          className="fixed w-full h-screen top-0 left-0 bg-modal-overlay z-overlay"
          onClick={(e) => {
            e.stopPropagation();
            setActiveRowIndex(null);
          }}
        />
        <div className="relative">
          <div
            className={`absolute ${rowIndex > 1 ? "bottom-0 -mb-5" : "top-0 -mt-2"} right-24 me-2 z-modal bg-white shadow-md border rounded p-2 w-40`}
          > */}
        <ul className="list-none p-0 m-0">
          {menuItems.map(({ key, icon, onClick }) => {
            const isDisabled =
              (key === "link_asset" && !cellData?.serial_number) ||
              (key === "voice_call" && !cellData?.cid) ||
              (key === "live_view" && !cellData?.cid) ||
              (key === "configuration" && !cellData?.cid);
            //  ||
            // (key === "live_view" && cellData?.is_online !== "online");
            return (
              <li
                key={key}
                className={`py-2 px-4 flex items-center text-center gap-2 ${isDisabled ? "pointer-events-none text-gray-400" : "hover:bg-gray-200 cursor-pointer"} `}
                onClick={(e) => {
                  e.stopPropagation();
                  closeContextMenu();
                  onClick?.();
                  // setActiveRowIndex(null);
                }}
              >
                <img
                  src={icon}
                  alt={key}
                  className={`w-5 h-5${isDisabled ? " grayscale opacity-50" : ""}`}
                />
                <p>{t(key)}</p>
              </li>
            );
          })}
        </ul>
        {/* </div>
        </div> */}
      </>
    );
  };

  const MemoizedGroupSelector = useMemo(() => {
    return (
      <>
        <ModalGroupCameraSelector
          hideGroupCameraToggle={false}
          allGroups={dataOrgGroups?.results ?? []}
          filteredGroupData={
            !!dataOrgGroups && !isFetchingOrgGroups
              ? (dataOrgGroups?.results?.map((group) => ({
                ...group,
                is_hgroup: false,
                checked: (selectedGroups as string[]).includes(group.groupid),
              })) as TGroupSelectorItem[])
              : []
          }
          setFilteredGroupData={(e: TGroupSelectorItem[]) => {
            return setSelectedGroups(
              e?.filter((group) => group.checked)?.map((group) => group.groupid)
            );
          }}
          key={`group-selector-${isFetchingOrgGroups.toString()}`} // -allSelected-${JSON.stringify(allGroupsSelected)}
          isConfigurationModal={isConfigurationModal}
          navigateToSettings={(
            selectedType,
            camera_ids,
            group_ids,
            isAllCameraSelected,
            isAllGroupSelected
          ) => {
            dispatch(
              setModalsData({
                ...modalsState,
                showPrimaryOverlay: true,
              })
            );
            // groups selected
            getDeviceListingAPITrigger({
              keyword: "",
              model: "",
              status: "",
              offset: 0,
              limit: APP_CONFIG.LISTINGS.XL_PAGE_SIZE,
              ...(selectedType === REPORT_FILTER_BY_OPTION_VALUES.CAMERA
                ? {
                  camera_ids: !!isAllCameraSelected
                    ? ""
                    : camera_ids?.length
                      ? camera_ids.join(",")
                      : "",
                  group_ids: undefined,
                }
                : {
                  group_ids: !!isAllGroupSelected
                    ? ""
                    : group_ids?.length
                      ? group_ids.join(",")
                      : "",
                  camera_ids: undefined,
                }),
            })
              .unwrap()
              .then((res: CameraListingResponse<DeviceListingResponse[]>) => {
                // navigate
                if (res?.data) {
                  const cameraIds = res?.data
                    ?.filter(
                      (device) =>
                        device.cid !== undefined &&
                        device.cid !== null &&
                        device.cid !== ""
                    )
                    ?.map((device, index) => device.cid);
                  if (APP_CONFIG.DEBUG.ALL)
                  console.log("A ===== CAMERAS COUNT", cameraIds.length);
                  dispatch(
                    setModalsData({
                      ...modalsState,
                      showGroupCameraSelector: false,
                      showPrimaryOverlay: false,
                    })
                  );
                  navigate(
                    routeUrls.dashboardChildren.cameraChildren.camera_settings + `${cameraIds.length === 1 ? "/" + cameraIds[0] : ""}`,
                    {
                      state: {
                        isSingleConfiguration: false,
                        isMassSingleConfiguration: cameraIds.length === 1 ? true : false,
                        cameraIds: cameraIds,
                      },
                    }
                  );
                }
              });
          }}
        />
      </>
    );
  }, [
    modalsState.showHierarchyGroupSelector,
    accountHasGroupHierarchyEnabled,
    dataOrgGroups,
    isFetchingOrgGroups,
    selectedGroups,
    isConfigurationModal,
    selectedDataType,
    selectedCameras,
    allGroupsSelected,
    allCamerasSelected,
  ]);

  const MemoizedCameraListingMap = useMemo(() => {
    const filteredDevices: DeviceListingResponseWAddress[] =
      !!results &&
        !!resultsWithAddress &&
        !!dataDeviceListing &&
        dataDeviceListing.count > 0
        ? resultsWithAddress.filter(
          (deviceItem) =>
            !!deviceItem.ft_lastvalidlatitude &&
            !!deviceItem.ft_lastvalidlongitude
        )
        : [];
    return (
      <CameraListingWidgetMap
        dataDeviceList={
          !!filteredDevices && filteredDevices.length > 0
            ? ({
              count: filteredDevices.length,
              data: filteredDevices,
            } as CameraListingResponse<DeviceListingResponseWAddress[]>)
            : undefined
        }
        isFetchingDeviceList={isFetchingDeviceListing}
        isMapCollapsed={expanded}
      />
    );
  }, [results, resultsWithAddress, dataDeviceListing, isFetchingDeviceListing]);

  // INTENTIONALLY using vehicle API to get drivers as here we will need to show the drivers
  // from those vehicles that have a camera associated with them
  // const {
  //   data: dataInitialCameraVehicleDriversList,
  //   isFetching: isFetchingInitialCameraVehicleDriversList,
  // } = useOrganizationVehiclesQuery({
  //   accountid: `${thisUserOrganizationId}`,
  //   page: 1,
  //   page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
  //   search: "",
  //   linked_to_camera: true,
  //   linked_to_driver: true,
  // });
  const [orgVehiclesQuery, { isLoading: isLazyLoadingVehicles }] =
    useLazyOrganizationVehiclesQuery();
  const loadOptionsHandlerDriver = async (
    inputValue: string,
    prevOptions: OptionsOrGroups<unknown, GroupBase<unknown>>,
    additional?: { page: number }
  ) => {
    // console.log("CALL prevOptions", prevOptions);
    const { data: cameraVehicleDriversQueryResponse } = await orgVehiclesQuery({
      accountid: `${thisUserOrganizationId}`,
      page: additional?.page ?? 1,
      page_size: APP_CONFIG.LISTINGS.DROPDOWN_SEARCH_PAGE_SIZE,
      search: inputValue,
      // search_fields: APP_CONFIG.ENTITY_SEARCH_FIELDS.DRIVER_DROPDOWN.join(","), // search by driverid only
      // disabled the above `search_fields` param, as we are intentionally calling vehicle API to get drivers, special set of `search_fields` will be used here:
      // search_fields:
      //   APP_CONFIG.ENTITY_SEARCH_FIELDS.DEVICE_DRIVER_SEARCH.join(","),
      // 'search_fields' intentionally not added here as default search fields are defined in the API service
      linked_to_camera: true,
      linked_to_driver: true,
    });

    if (
      !cameraVehicleDriversQueryResponse ||
      !cameraVehicleDriversQueryResponse?.results
    ) {
      return {
        options: [],
        hasMore: false,
        additional: {
          page: additional?.page || 1,
        },
      };
    }
    const { results } = cameraVehicleDriversQueryResponse as {
      results: TLegacyVehicle[];
    };

    // Filter duplicates based on driverid before mapping
    const uniqueResults = results.filter(
      (item, index, self) =>
        index === self.findIndex((t) => t.driverid === item.driverid)
    );
    return {
      options: !!uniqueResults
        ? (uniqueResults || ([] as TLegacyVehicle[])).map(
          (item: TLegacyVehicle, index: number) =>
            ({
              value: `${item.driverid}`,
              label: `${item.drivername || item.driverid}${APP_CONFIG.DEBUG.ALL ? ` (${item.driverid})` : ""}`, // driverDisplayName(item),
            }) as TSelectboxOption
        )
        : [],
      hasMore: cameraVehicleDriversQueryResponse?.next ? true : false,
      additional: {
        page: additional?.page ? (additional?.page as number) + 1 : 2,
      },
    };
  };
  const debouncedLoadOptionsHandlerDriver = asyncDebounce(
    loadOptionsHandlerDriver,
    APP_CONFIG.DEBOUNCE.SEARCH
  );

  const isDisabled =
    !cameraManagementQueryParams?.keyword &&
    !cameraManagementQueryParams?.model &&
    !cameraManagementQueryParams?.status &&
    !cameraManagementQueryParams?.driver_id;

  return (
    <div
      className={
        displayAsDashWidget
          ? "flex flex-col rounded-lg min-h-96 overflow-hidden relative bg-white border-2 shadow-lg"
          : ""
      }
    >
      {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
        <pre>
          assetId: {JSON.stringify(modalsState.voiceCallAssetId)}
          <br />
          driverName: {JSON.stringify(modalsState.voiceCallDriverName)}
        </pre>
      )}
      {/* map */}
      {/* moved wrapper <div className="h-96" /> to cameraListingWidgetMap.tsx to add 'close streetview on clicking outside map' behaviour */}
      {!!displayAsDashWidget && <>{MemoizedCameraListingMap}</>}

      {/* Expand / Collapse Button */}
      {displayAsDashWidget && (
        <div className="relative">
          <AdminFormFieldSubmit
            type="button"
            label={
              <>
                <span className="hidden xl:block mr-2">
                  {t(expanded ? "collapse" : "expand")}
                </span>
                <img
                  src={svgIcons.ChevronDown}
                  alt="fast-forward-icon"
                  className={`size-6 -mx-2 transition-all${expanded ? " rotate-180" : ""} group-focus:grayscale-0 group-focus:brightness-0 group-focus:invert group-hover:grayscale-0 group-hover:brightness-0 group-hover:invert`}
                />
              </>
            }
            variant="primary"
            customWrapperClass="absolute bottom-4 right-4"
            onClick={() => setExpanded(!expanded)}
          />
        </div>
      )}

      {/* Listing */}
      {
        // display if not widget
        (!displayAsDashWidget || (!!displayAsDashWidget && expanded)) && (
          <div className={`px-5 pt-4 bg-gray-100`}>
            {displayAsDashWidget && APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
              <pre className="absolute top-0 right-10 bg-amber-500">
                camera-management.tsx
              </pre>
            )}

            <div className="w-full Xgrid Xgrid-cols-12 flex gap-4">
              <AppSearchBox
                placeholder={t("search_placeholder_camera")}
                initialValue={cameraManagementQueryParams.keyword}
                controlledValue={searchText}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  debouncedSetSearchKeyword(e.target.value);
                  setSearchText(e.target.value);
                }}
                onClear={() => {
                  debouncedSetSearchKeyword("");
                  setSearchText("");
                }}
                inputClassName="w-full h-11 rounded-md border-gray-200 py-2.5 ps-10 shadow-sm sm:text-sm"
                wrapperClassName="flex-grow mt-1"
              />
              <AdminFormFieldDropdown
                id="status-select"
                label=""
                name={t("select_status_name")}
                placeholder={t("select_status_placeholder")}
                value={cameraManagementQueryParams?.status}
                onChange={(option) => {
                  if (option) {
                    setCameraManagementQueryParams({
                      ...cameraManagementQueryParams,
                      status: option?.value,
                      offset: 0,
                    });
                  }
                }}
                customWrapperClass="flex-grow"
                dimensionClass="w-full h-11"
                options={STATUS_OPTIONS}
                key={`status-select-${cameraManagementQueryParams?.status}`}
              />
              {displayAsDashWidget !== true && (
                <>
                  <AdminFormFieldDropdown
                    id="model-select"
                    label=""
                    name={t("select_model_name")}
                    placeholder={t("select_model_placeholder")}
                    value={cameraManagementQueryParams?.model}
                    onChange={(option) => {
                      setCameraManagementQueryParams({
                        ...cameraManagementQueryParams,
                        model: option?.value || "",
                        offset: 0,
                      });
                    }}
                    customWrapperClass="flex-grow"
                    dimensionClass="w-full h-11"
                    options={allAvailableDeviceModels?.data?.map(
                      (item: Model) => ({
                        label: CONVERT_CAMERA_MODEL_NAMES
                          ? item.name.replace("K", "FV")
                          : item.name,
                        value: item.name,
                      })
                    )}
                    key={`model-select-${cameraManagementQueryParams?.model}`}
                  />
                </>
              )}

              {/* Filter Groups */}
              <button
                className={`flex bg-white items-center gap-2 px-4 h-11 rounded-lg hover:bg-accent-blue-pale mt-1`}
                onClick={() => {
                  setIsConfigurationModal(false);
                  dispatch(
                    setModalsData({
                      ...modalsState,
                      showGroupCameraSelector: true,
                    })
                  );
                }}
              >
                <img src={svgIcons.FilterIcon} alt="filter" />
                <span className="text-accent-blue-dark">
                  {t("filter_groups")}
                </span>
              </button>

              {/* Filter by Driver */}
              {showDriver && (
                <AdminFormFieldAsyncLazyDropdown
                  key={`driver_id_filter_${JSON.stringify(cameraManagementQueryParams)}`}
                  customWrapperClass="flex-grow min-w-36 mt-1"
                  loadingData={isLazyLoadingVehicles}
                  label={false}
                  id="driver_id"
                  name="driver_id"
                  value={
                    !!cameraManagementQueryParams.driver_id
                      ? cameraManagementQueryParams.driver_id
                      : undefined
                  } // passed undefined to let placeholder display if no value is selected
                  placeholder={t("driver_id_placeholder")}
                  // defaultOptions={
                  //   dataInitialCameraVehicleDriversList?.results?.map(
                  //     (item: TLegacyVehicle) => ({
                  //       value: `${item.driverid}`,
                  //       label: `${item.driverid}${item.drivername}`, // driverDisplayName(item),
                  //     })
                  //   ) || undefined
                  // }
                  defaultOption={
                    !!cameraManagementQueryParams.driver_id
                      ? {
                        value: cameraManagementQueryParams.driver_id,
                        label:
                          cameraManagementQueryParams.driver_name ||
                          cameraManagementQueryParams.driver_id,
                      }
                      : undefined
                  } // passed undefined to let placeholder display if no value is selected
                  additional={{ page: 1 }}
                  isClearable={true}
                  HideDropdownIndicator={false}
                  loadOptions={debouncedLoadOptionsHandlerDriver}
                  noOptionsMessage={() => t("driver_id_no_item")}
                  onChange={(e) => {
                    if (APP_CONFIG.DEBUG.ALL) console.log("driver_id", e);
                    setCameraManagementQueryParams({
                      ...cameraManagementQueryParams,
                      driver_id: e?.value,
                      driver_name: e?.label,
                      linked_to_driver: !!e?.value,
                    });
                  }}
                // onBlur={(e) => {
                //   formikSetTouched("driver_id", true);
                //   handleBlur(e);
                // }}
                // error={errors.driver_id}
                // touched={touched.driver_id}
                // disabled={!userCanEdit}
                // detailsFormField={true}
                />
              )}
              <AdminFormFieldSubmit
                buttonDimensionClass={"h-11 w-full px-2 group"}
                customWrapperClass="w-28 mt-1"
                type="button"
                variant="primary"
                label={
                  <span className="flex justify-center whitespace-nowrap">
                    {t("clear")}
                    {
                      <img
                        className={`ml-2 ${!isDisabled ? "transition-all group-focus:grayscale-0 group-focus:brightness-0 group-focus:invert group-hover:grayscale-0 group-hover:brightness-0 group-hover:invert" : ""} h-5 w-5`}
                        src={
                          isDisabled
                            ? svgIcons.CloseIconGray
                            : svgIcons.CloseIcon
                        }
                        alt={"clear"}
                      />
                    }
                  </span>
                }
                onClick={() => {
                  setCameraManagementQueryParams({
                    ...cameraManagementQueryParams,
                    model: "",
                    status: "",
                    keyword: "",
                    driver_id: undefined,
                    driver_name: undefined,
                    linked_to_driver: false,
                    offset: 0,
                  });
                  setSearchText("");
                }}
                disabled={isDisabled}
              />
              {displayAsDashWidget !== true && (
                <button
                  className={`flex bg-white items-center gap-2 px-4 h-11 rounded-lg hover:bg-accent-blue-pale mt-1`}
                  onClick={() => {
                    setIsConfigurationModal(true);
                    dispatch(
                      setModalsData({
                        ...modalsState,
                        showGroupCameraSelector: true,
                      })
                    );
                  }}
                >
                  <img src={svgIcons.FilterIcon} alt="filter" />
                  <span className="text-accent-blue-dark">
                    {t("configure")}
                  </span>
                </button>
              )}
            </div>
            <ModalEditListingColumns
              columns={columns}
              setColumns={setColumns}
              lsKey={
                displayAsDashWidget
                  ? localStorageKeys.columns.CameraListingWidget
                  : localStorageKeys.columns.CameraManagement
              }
              modalType={
                displayAsDashWidget
                  ? "showEditColumnsDashboard"
                  : "showEditColumns"
              }
            />
            {/* Sorted by label */}
            {!!cameraManagementQueryParams?.ordering && (
              <div className="flex justify-start font-bold mt-4">
                <SortingStatusWithReset
                  ordering={cameraManagementQueryParams.ordering}
                  columnOrderingFields={columnOrderingFields}
                  onResetOrdering={() => handleOrderingChange(undefined)}
                />
              </div>
            )}
            <div className="py-4 relative">
              <EditListingColumnsButton
                modalType={
                  displayAsDashWidget
                    ? "showEditColumnsDashboard"
                    : "showEditColumns"
                }
              />
              <AdminTable
                viewportHeightOffset={"400"}
                columns={columns
                  .filter((item) => item?.show)
                  .map((item) => item.name)}
                data={tableData}
                isLoading={isFetchingDeviceListing}
                listingQueryParams={cameraManagementQueryParams}
                onOrderingChange={handleOrderingChange}
                ordering={cameraManagementQueryParams.ordering}
                sortableColumns={columnOrderingFields}
              />
              {!isFetchingDeviceListing && (
                <Pagination
                  pageSize={cameraManagementQueryParams.limit}
                  handlePageSizeChange={(e: TSelectboxOption | null) => {
                    setCameraManagementQueryParams({
                      ...cameraManagementQueryParams,
                      offset: 0,
                      limit: parseInt(`${e?.value}`),
                    });
                  }}
                  totalPages={
                    count
                      ? Math.ceil(count / cameraManagementQueryParams.limit)
                      : 1
                  }
                  // forcePage={cameraManagementQueryParams.page - 1}
                  forcePage={
                    !!cameraManagementQueryParams.offset
                      ? Math.floor(
                        cameraManagementQueryParams.offset /
                        cameraManagementQueryParams.limit
                      )
                      : 0
                  }
                  handlePageClick={(data: TPaginationSelected) => {
                    setCameraManagementQueryParams({
                      ...cameraManagementQueryParams,
                      offset: data.selected * cameraManagementQueryParams.limit,
                    });
                  }}
                  // paginCurrentPage={cameraManagementQueryParams.page}
                  paginCurrentPage={
                    !!cameraManagementQueryParams.offset
                      ? Math.floor(
                        cameraManagementQueryParams.offset /
                        cameraManagementQueryParams.limit
                      ) + 1
                      : 1
                  }
                  paginItemsOnCurrentPage={results?.length || 0}
                  paginTotalItemsCount={count}
                />
              )}
            </div>
            {!!activeRow?.data?.serial_number && (
              <ModalLinkAsset
                cameraId={activeRow?.data?.serial_number}
                assetId={activeRow?.data?.ft_device_id || ""}
                onSuccess={refetch}
              />
            )}

            {/* Liveview and Voicecall modals are moved to `src/components/common/VMCameraVoicecalling.tsx` */}

            {!!dataOrgGroups && !isFetchingOrgGroups && (
              <>{MemoizedGroupSelector}</>
            )}

            {/* Wakeup camera modal is moved to `src/components/common/VMCameraVoicecalling.tsx` */}

            <ParkingPhotos photos={activeRow?.data?.snapshot || []} />
          </div>
        )
      }
    </div>
  );
};

export default ScreenCameraManagement;
